import React, {useEffect} from 'react';
import {HeaderSearch} from './header_search/HeaderSearch';
import HeaderUser from 'components/header/header_user/HeaderUser';
import {Link, useNavigate} from 'react-router-dom';
import {Dropdown, Nav} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import {UserPreferences} from '@/views/settings/stores/user_preferences';
import useData from '@/utils/useData';
import CellLoader from '@/components/loading/CellLoader';
import {buildURL} from '@/utils/urlBuilder';
import axios from 'axios';
import i18n from 'i18n';
import {useStoreState, useStoreActions} from 'store';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {usePlatformPermissions} from '@/utils/platformPermissions';
import {Toggle} from '@/components/ui';
import Icon from '@/components/icons/Icon';


export const HeaderBar = React.memo(() => {
    const navigate = useNavigate();
    const appConfig = useStoreState((state) => state.appConfigs.items);
    const menuStructure = useStoreState((state) => state.sidebarMenu.menuStructure);
    const {config, userData, loading: configLoading} = useOptimizedConfig();
    const platformPermissions = usePlatformPermissions(userData);
    const [preferences, loadingPreferences, updatePreferences] = useData<UserPreferences>(config?.URLS?.userPreferencesResource || '', {
        useLocalStore: true,
        raiseErrors: false,
    })
    const setUserDataReload = useStoreActions((actions) => actions.userData.setReload);

    // Get brand link URL with fallback
    const getBrandLinkUrl = () => {
        try {
            // Try to build the general story URL first
            return buildURL('specialStoryDetailView', {story_slug: 'general'});
        } catch (error) {
            // Fallback to a safe default
            return '/app/discover/my-stories';
        }
    };

    useEffect(() => {
        const handleBackdropClick = (e: MouseEvent) => {
            const body = document.body;
            const target = e.target as HTMLElement;
            if (body.classList.contains('show-sidebar') &&
                target &&
                (target === body || target.classList.contains('mini-sidebar')) &&
                !target.closest('.left-sidebar') &&
                !target.closest('.nav-toggler')) {
                body.classList.remove('show-sidebar');
            }
        };

        document.addEventListener('click', handleBackdropClick);
        return () => {
            document.removeEventListener('click', handleBackdropClick);
        };
    }, []);

    const toggleNavbar = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        document.body.classList.toggle('show-sidebar');
    }

    const handleDataAnonymizationToggle = async (checked: boolean) => {
        try {
            // Update local state immediately for responsive UI
            const updatedPreferences = {
                ...preferences,
                dataAnonymized: checked,
            };
            updatePreferences(updatedPreferences);

            // Save to server
            await axios.post(config?.URLS?.userPreferencesResource || '', updatedPreferences);

            // Broadcast updated preferences so other views (e.g. Settings) can sync their UI
            try {
                window.dispatchEvent(new CustomEvent('userPreferencesUpdated', {detail: updatedPreferences}));
            } catch (e) {
                console.warn('userPreferencesUpdated event dispatch failed', e);
            }

            // Trigger userData reload to update indexAnonymized
            setUserDataReload(true);
        } catch (error) {
            console.error('Error updating preferences:', error);
            // Revert local state on error
            updatePreferences(preferences);
        }
    };

    const userInitials = () => {
        if (!userData?.email) {
            return 'U'; // Default initial if email is not available
        }
        try {
            return userData.email.split('@')[0].split('.')[0][0].toUpperCase();
        } catch (error) {
            console.warn('Error getting user initials:', error);
            return 'U'; // Fallback initial
        }
    }

    // Keep header preferences in sync with changes saved from the Preferences view
    useEffect(() => {
        const handler = (event: Event) => {
            // `detail` contains the full UserPreferences object from PreferencesView
            const detail = (event as any).detail;
            if (detail) {
                updatePreferences(detail);
            }
        };

        window.addEventListener('userPreferencesUpdated', handler as EventListener);
        return () => {
            window.removeEventListener('userPreferencesUpdated', handler as EventListener);
        };
    }, [updatePreferences]);


    let maskingBtn;
    if (preferences) {
        // Check the user permissions to see if the user can disable data anonymization
        const canDisableMasking = platformPermissions.canAccessUnanonymizedIndex;
        if (canDisableMasking) {
            maskingBtn = (
                <>
                    <Nav.Item className="d-flex align-items-center mt-2 px-2" style={{fontSize: '16px'}}>
                        <div className='d-flex align-items-center' id='masking-toggle-container'>

                            <Toggle
                                checked={preferences?.dataAnonymized || false}
                                onChange={handleDataAnonymizationToggle}
                                id='masking-toggle'
                                data-cy='masking-toggle'
                                size="sm"
                            />
                            <span className="ml-2 mr-2">
                                {preferences?.dataAnonymized ? i18n.t('hd_masking_on.text') : i18n.t('hd_masking_off.text')}
                            </span>
                        </div>
                    </Nav.Item>
                </>
            );
        } else {
            maskingBtn = (
                <Tooltip
                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                    placement='bottom'
                >
                    <Nav.Item>
                        <Nav.Link className='mt-2' style={{fontSize: '16px'}}>
                            <div>
                                <Icon name="mdi-toggle-switch" style={{color: '#009efb'}} className="mr-1" />
                                {i18n.t('hd_masking_on.text')}
                            </div>
                        </Nav.Link>
                    </Nav.Item>
                </Tooltip>
            );
        }
    }

    if (configLoading || !config || loadingPreferences) {
        return (
            <CellLoader text={i18n.t('loading_header.text')} />
        );
    }

    return (
        <header className='topbar' style={{zIndex: 1000}}>
            <nav className='navbar top-navbar navbar-expand-md navbar-light'>
                <div className='navbar-header'>
                    <Link
                        to={getBrandLinkUrl()}
                        className='navbar-brand'
                        onClick={(e) => {
                            e.preventDefault();
                            navigate(getBrandLinkUrl());
                        }}
                    >
                        <b>
                            <img
                                width='70%'
                                height='70%'
                                src={`${config.FCONST.STATIC_URL}theme/assets/images/FS-logo-element-33x34.png`}
                                alt={i18n.t('hd_alt_home.text')}
                                className='dark-logo' />
                        </b>
                        <span className='hidden-sm-down'>
                            <img
                                width='65%'
                                height='65%'
                                src={`${config.FCONST.STATIC_URL}theme/assets/images/FS-logo-text.png`}
                                alt={i18n.t('hd_alt_home.text')}
                                className='dark-logo' />
                        </span>
                    </Link>
                </div>
                <div className='navbar-collapse'>
                    <ul className='navbar-nav mr-auto mt-md-0'>
                        <li className='nav-item'>
                            <a
                                href="#"
                                className='nav-link nav-toggler text-muted waves-effect waves-dark px-4 mt-2'
                                onClick={toggleNavbar}
                                style={{cursor: 'pointer'}}
                            >
                                <Icon name='mdi-menu' />
                            </a>
                        </li>

                    </ul>

                    <div className='flex-grow-1 d-flex mt-1 ml-2 mr-2 hidden-sm-down'>
                        <HeaderSearch />
                    </div>

                    <Nav as='ul' className='navbar-nav'>
                        {/* Other Navbar items */}
                        {maskingBtn}

                        <Nav.Item className='hidden-sm-down'>
                            <Nav.Link
                                href={config.URLS.helpSite}
                                target='_blank'
                                className='mt-2'
                                data-cy="knowledge-base-btn"
                            >
                                <Icon name='mdi-help-circle' style={{fontSize: '1.75rem'}} />
                            </Nav.Link>
                        </Nav.Item>

                        <Dropdown as={Nav.Item}>
                            <Dropdown.Toggle
                                as={Nav.Link}
                                bsPrefix='nav-link dropdown-toggle text-muted waves-effect waves-dark mt-2'
                                data-cy='user-dropdown-toggle'
                            >
                                <div data-initials={userInitials()}
                                ></div>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" data-cy="user-dropdown-menu">
                                <ul className='dropdown-user'>
                                    <Dropdown.Item as='li' bsPrefix=' ' data-cy="header-bar-btn-1">
                                        <HeaderUser />
                                    </Dropdown.Item>

                                    <Dropdown.Divider />

                                    <Dropdown.Item as='li' bsPrefix=' ' data-cy="header-bar-btn">
                                        <a href={config.URLS.FSAccountsService} target='_blank'
                                            data-cy="change-password-toggle" rel="noreferrer">
                                            <Icon name='mdi-lock-reset' className='mr-1' />
                                            {i18n.t('hd_change_psd.text')}
                                        </a>
                                    </Dropdown.Item>

                                    <Dropdown.Divider />

                                    <Dropdown.Item as='li' bsPrefix=' ' data-cy='user-dropdown-logout-button'>
                                        <a href={config.URLS.logOutView}>
                                            <Icon name="mdi-power" className="mr-1" />
                                            {i18n.t('hd_logout.text')}
                                        </a>
                                    </Dropdown.Item>
                                </ul>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Nav>
                </div>
            </nav>
        </header>
    );
});
